/* The web store's copy of stylon-salon-portal/src/utils/zoom.js — keep the two in step (DESIGN_SYSTEM.md §6). */
/**
 * ZOOM — from the item into its page, and back to its place (DESIGN_SYSTEM §6.4–5).
 *
 * zoomInto(card, update): the page that `update` shows grows out of the card's box. Where the browser has
 * same-document View Transitions the new page is clipped from the card to the screen; elsewhere, or with reduced
 * motion, `update` simply runs. A transition the browser refuses never stops the page from opening.
 *
 * zoomBackTo(card): coming back (the browser's back has already drawn the page underneath, so nothing old is left to
 * capture), a frame of the screen shrinks into the card and fades — the person sees where the page went. Nothing is
 * drawn when the card is gone or off screen.
 *
 * Only transform / opacity / clip-path move (§6.6). 300 ms, no overshoot.
 */
export const ZOOM_MS = 300;
export const ZOOM_EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
const RADIUS = 14;

let origin = null;

export function rememberOrigin(o) {
  origin = o || null;
}

export function peekOrigin() {
  return origin;
}

export function takeOrigin() {
  const o = origin;
  origin = null;
  return o;
}

/**
 * The new page has rendered. A router that commits navigations in a transition (React Router's v7_startTransition)
 * leaves the old page in the DOM after flushSync, so a zoom that captured right away would grow the OLD page
 * (review 10-22). The opener returns pageRendered() from its update; the router's layout effect calls
 * markPageRendered() when the new location commits. The timeout keeps a page that never commits from holding the
 * screen.
 */
let rendered = null;

export function pageRendered(timeoutMs = 1200) {
  if (rendered) rendered.resolve('superseded');
  return new Promise((resolve) => {
    const t = setTimeout(() => {
      if (rendered && rendered.resolve === done) rendered = null;
      resolve('timeout');
    }, timeoutMs);
    function done(why) {
      clearTimeout(t);
      resolve(why);
    }
    rendered = { resolve: done };
  });
}

export function markPageRendered() {
  const r = rendered;
  rendered = null;
  if (r) r.resolve('rendered');
}

export function reducedMotion() {
  try {
    return !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  } catch (_) {
    return false;
  }
}

export function insetFor(r, radius = RADIUS) {
  const w = window.innerWidth;
  const h = window.innerHeight;
  const px = (n) => `${Math.max(0, Math.round(n))}px`;
  return `inset(${px(r.top)} ${px(w - r.right)} ${px(h - r.bottom)} ${px(r.left)} round ${radius}px)`;
}

function onScreen(r) {
  return !!r && r.width > 0 && r.height > 0 && r.bottom > 0 && r.right > 0 && r.top < window.innerHeight && r.left < window.innerWidth;
}

export function zoomInto(card, update) {
  const r = card?.getBoundingClientRect?.();
  if (!onScreen(r) || reducedMotion() || typeof document.startViewTransition !== 'function') {
    update();
    return;
  }
  let ran = false;
  // The update may return a promise (the new page rendered): the browser keeps the old picture until it settles.
  const run = () => {
    ran = true;
    return update();
  };
  // Marked while it runs: the stylesheet holds the old page still and lets this code move the new one, for this
  // transition only (index.css, `html.zooming::view-transition-*`).
  const root = document.documentElement;
  const unmark = () => root.classList.remove('zooming');
  root.classList.add('zooming');
  let vt;
  try {
    vt = document.startViewTransition(run);
  } catch (_) {
    unmark();
    if (!ran) update();
    return;
  }
  Promise.resolve(vt?.finished).then(unmark, unmark);
  const from = insetFor(r);
  Promise.resolve(vt?.ready)
    .then(() => document.documentElement.animate(
      { clipPath: [from, 'inset(0px 0px 0px 0px round 0px)'] },
      { duration: ZOOM_MS, easing: ZOOM_EASE, pseudoElement: '::view-transition-new(root)' },
    ))
    .catch(() => {});
}

export function zoomBackTo(card) {
  const r = card?.getBoundingClientRect?.();
  if (!onScreen(r) || reducedMotion()) return Promise.resolve();
  const ghost = document.createElement('div');
  ghost.setAttribute('data-zoom-ghost', '');
  ghost.setAttribute('aria-hidden', 'true');
  Object.assign(ghost.style, {
    position: 'fixed', inset: '0', zIndex: '2147483000', pointerEvents: 'none',
    background: 'var(--c-surface, #fff)', boxShadow: '0 8px 40px rgba(15,27,51,0.18)',
  });
  document.body.appendChild(ghost);
  const done = () => ghost.remove();
  try {
    const a = ghost.animate(
      [{ clipPath: 'inset(0px 0px 0px 0px round 0px)', opacity: 1 }, { clipPath: insetFor(r), opacity: 0 }],
      { duration: ZOOM_MS, easing: ZOOM_EASE, fill: 'forwards' },
    );
    return Promise.resolve(a?.finished).then(done, done);
  } catch (_) {
    done();
    return Promise.resolve();
  }
}
